import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Chip, 
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  Alert,
  Paper,
  Avatar,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  LinearProgress,
  useTheme,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Tabs,
  Tab,
  Fab
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Add as AddIcon, 
  MoreVert as MoreVertIcon,
  AccessTime as AccessTimeIcon,
  Mic as MicIcon,
  Share as ShareIcon,
  DeleteOutline as DeleteIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  PeopleAlt as PeopleAltIcon,
  CheckCircle as CheckCircleIcon,
  Help as HelpIcon,
  Pending as PendingIcon,
  CalendarToday as CalendarTodayIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
  AutoAwesome as AutoAwesomeIcon,
  Subject as SubjectIcon,
  Visibility as VisibilityIcon,
  FileCopy as FileCopyIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import meetingService from '../services/meetingService';
import projectService from '../services/projectService';

import userService from '../services/userService';
import summaryService from '../services/summaryService';
import transcriptionService from '../services/transcriptionService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { auth } from '../services/firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, WidthType } from 'docx';
import { saveAs } from 'file-saver';
import { markdownToHtml } from '../utils/markdownTiptap';
import { getCurrentUserId } from '../utils/authUtils';
import { db } from '../services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import EmailDialog from '../components/common/EmailDialog';
import NewMeetingDialog from '../components/common/NewMeetingDialog';
import EditMeetingDialog from '../components/common/EditMeetingDialog';
import BaseDialog from '../components/common/BaseDialog';
import BaseButton from '../components/common/BaseButton';
import UniversalCard from '../components/common/UniversalCard';

// Utilitaires pour créer des documents Word (conservés de l'ancien code)
async function createSummaryWordDocument(meeting, summary) {
  const font = 'Inter';
  const fallbackFont = 'Segoe UI, Roboto, Arial, sans-serif';
  const docChildren = [];
  
  // Fonction pour formater la date au format jjMMAAAA
  const formatDateForFilename = (date) => {
    if (!date) return 'SansDate';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'SansDate';
    
    const day = d.getDate().toString().padStart(2, '0');
    const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    
    return `${day}${month}${year}`;
  };
  
  // En-tête moderne avec icône et branding
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 200 },
      children: [
        new TextRun({
          text: '✨ TRANSCRIPTION AUTOMATIQUE',
          size: 16,
          font: fallbackFont,
          color: '6366F1',
          bold: true,
          allCaps: true,
        }),
      ],
    })
  );

  // Titre principal avec emoji
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: '📋 ',
          size: 48,
          font: fallbackFont,
        }),
        new TextRun({
          text: 'RÉSUMÉ DE RÉUNION',
          bold: true,
          size: 32,
          font: fallbackFont,
          color: '1E293B',
        }),
      ],
    })
  );

  // Titre de la réunion avec style moderne
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: meeting.title || 'Réunion sans titre',
          bold: true,
          size: 24,
          font: fallbackFont,
          color: '475569',
        }),
      ],
    })
  );

  // Ligne de séparation décorative
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: '◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆',
          size: 14,
          font: fallbackFont,
          color: 'CBD5E1',
        }),
      ],
    })
  );

  // Section informations avec icônes
  const infoDate = meeting.date ? format(new Date(meeting.date), "EEEE d MMMM yyyy", { locale: fr }) : 'Date non spécifiée';
  
  // Tableau d'informations stylé
  const infoRows = [
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: '📅 Date',
                  bold: true,
                  size: 20,
                  font: fallbackFont,
                  color: '3B82F6',
                }),
              ],
            })
          ],
          width: { size: 30, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
          shading: { fill: 'F8FAFC' },
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: infoDate,
                  size: 20,
                  font: fallbackFont,
                  color: '64748B',
                }),
              ],
            })
          ],
          width: { size: 70, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
        }),
      ],
    }),
  ];

  // Ajouter la durée si disponible
  if (meeting.duration) {
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '⏱️ Durée',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: '10B981',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F8FAFC' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: meeting.duration,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter les participants si disponibles
  if (meeting.participants && meeting.participants.length > 0) {
    const participantsText = meeting.participants
      .map(p => typeof p === 'string' ? p : p.displayName || p.name || 'Participant')
      .join(', ');
    
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '👥 Participants',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: 'F59E0B',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F8FAFC' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: participantsText,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter le tableau d'informations
  docChildren.push(
    new Table({
      rows: infoRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: 'single', size: 2, color: 'E2E8F0' },
        bottom: { style: 'single', size: 2, color: 'E2E8F0' },
        left: { style: 'single', size: 2, color: 'E2E8F0' },
        right: { style: 'single', size: 2, color: 'E2E8F0' },
        insideHorizontal: { style: 'single', size: 1, color: 'F1F5F9' },
        insideVertical: { style: 'single', size: 1, color: 'F1F5F9' },
      },
    })
  );

  // Espacement avant le contenu
  docChildren.push(
    new Paragraph({
      spacing: { before: 600, after: 400 },
      children: [
        new TextRun({
          text: '',
        }),
      ],
    })
  );

  // Titre de section résumé
  docChildren.push(
    new Paragraph({
      spacing: { before: 300, after: 300 },
      children: [
        new TextRun({
          text: '🎯 ',
          size: 28,
          font: fallbackFont,
        }),
        new TextRun({
          text: 'RÉSUMÉ EXÉCUTIF',
          bold: true,
          size: 24,
          font: fallbackFont,
          color: '1E293B',
          allCaps: true,
        }),
      ],
    })
  );

  // Ligne sous le titre
  docChildren.push(
    new Paragraph({
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: '▔'.repeat(50),
          size: 16,
          font: fallbackFont,
          color: '6366F1',
        }),
      ],
    })
  );

  // Contenu du résumé avec conversion markdown
  if (summary && summary.text) {
    try {
      const html = await markdownToHtml(summary.text);
      const summaryParagraphs = htmlToDocxParagraphsModern(html, fallbackFont);
      docChildren.push(...summaryParagraphs);
    } catch (error) {

      // Fallback: traiter comme du texte simple
      const lines = summary.text.split('\n').filter(line => line.trim());
      lines.forEach((line, index) => {
        // Détecter les titres markdown
        if (line.startsWith('# ')) {
          docChildren.push(
            new Paragraph({
              spacing: { before: 300, after: 200 },
              children: [
                new TextRun({
                  text: line.substring(2),
                  bold: true,
                  size: 22,
                  font: fallbackFont,
                  color: '1E293B',
                }),
              ],
            })
          );
        } else if (line.startsWith('## ')) {
          docChildren.push(
            new Paragraph({
              spacing: { before: 250, after: 150 },
              children: [
                new TextRun({
                  text: line.substring(3),
                  bold: true,
                  size: 20,
                  font: fallbackFont,
                  color: '475569',
                }),
              ],
            })
          );
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          docChildren.push(
            new Paragraph({
              spacing: { after: 100 },
              indent: { left: 360 },
              children: [
                new TextRun({
                  text: '• ',
                  bold: true,
                  size: 18,
                  font: fallbackFont,
                  color: '6366F1',
                }),
                new TextRun({
                  text: line.substring(2),
                  size: 18,
                  font: fallbackFont,
                  color: '475569',
                }),
              ],
            })
          );
        } else if (line.trim()) {
          docChildren.push(
            new Paragraph({
              spacing: { after: 150 },
              children: [
                new TextRun({
                  text: line,
                  size: 18,
                  font: fallbackFont,
                  color: '475569',
                  
                }),
              ],
            })
          );
        }
      });
    }
  } else {
    docChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: 'Aucun résumé disponible pour cette réunion.',
            italics: true,
            size: 18,
            font: fallbackFont,
            color: '94A3B8',
          }),
        ],
      })
    );
  }

  // Espacement avant le pied de page
  docChildren.push(
    new Paragraph({
      spacing: { before: 800 },
      children: [
        new TextRun({
          text: '',
        }),
      ],
    })
  );

  // Ligne de séparation avant pied de page
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { before: 400, after: 200 },
      children: [
        new TextRun({
          text: '◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆',
          size: 14,
          font: fallbackFont,
          color: 'CBD5E1',
        }),
      ],
    })
  );

  // Pied de page élégant
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { before: 200 },
      children: [
        new TextRun({
          text: '🤖 Document généré automatiquement par l\'IA de Transcription le ',
          size: 16,
          italics: true,
          font: fallbackFont,
          color: '64748B',
        }),
        new TextRun({
          text: format(new Date(), "d MMMM yyyy 'à' HH:mm", { locale: fr }),
          size: 16,
          italics: true,
          bold: true,
          font: fallbackFont,
          color: '6366F1',
        }),
      ],
    })
  );

  return new Document({
    styles: {
      default: {
        document: {
          run: {
            font: fallbackFont,
            size: 18,
            color: '475569',
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        children: docChildren,
      },
    ],
  });
}

async function createTranscriptWordDocument(meeting, transcription) {
  const font = 'Inter';
  const fallbackFont = 'Segoe UI, Roboto, Arial, sans-serif';
  const docChildren = [];
  
  // En-tête moderne avec icône et branding
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 200 },
      children: [
        new TextRun({
          text: '🎙️ TRANSCRIPTION AUTOMATIQUE',
          size: 16,
          font: fallbackFont,
          color: '059669',
          bold: true,
          allCaps: true,
        }),
      ],
    })
  );

  // Titre principal avec emoji
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: '📝 ',
          size: 48,
          font: fallbackFont,
        }),
        new TextRun({
          text: 'TRANSCRIPTION COMPLÈTE',
          bold: true,
          size: 32,
          font: fallbackFont,
          color: '1E293B',
        }),
      ],
    })
  );

  // Titre de la réunion
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: meeting.title || 'Réunion sans titre',
          bold: true,
          size: 24,
          font: fallbackFont,
          color: '475569',
        }),
      ],
    })
  );

  // Ligne de séparation décorative
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: '◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆',
          size: 14,
          font: fallbackFont,
          color: 'CBD5E1',
        }),
      ],
    })
  );

  // Section informations avec icônes
  const infoDate = meeting.date ? format(new Date(meeting.date), "EEEE d MMMM yyyy", { locale: fr }) : 'Date non spécifiée';
  
  // Calculer la durée à partir de la transcription si possible
  let durationText = 'Non calculée';
  if (transcription?.utterances?.length > 0) {
    const first = transcription.utterances[0];
    const last = transcription.utterances[transcription.utterances.length - 1];
    if (last?.end && first?.start) {
      const durationSeconds = (last.end - first.start) / 1000;
      const hours = Math.floor(durationSeconds / 3600);
      const minutes = Math.floor((durationSeconds % 3600) / 60);
      const seconds = Math.floor(durationSeconds % 60);
      if (hours > 0) {
        durationText = `${hours}h ${minutes}min ${seconds}s`;
      } else {
        durationText = `${minutes}min ${seconds}s`;
      }
    }
  } else if (meeting.duration) {
    durationText = meeting.duration;
  }

  // Tableau d'informations stylé
  const infoRows = [
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: '📅 Date',
                  bold: true,
                  size: 20,
                  font: fallbackFont,
                  color: '059669',
                }),
              ],
            })
          ],
          width: { size: 30, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
          shading: { fill: 'F0FDF4' },
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: infoDate,
                  size: 20,
                  font: fallbackFont,
                  color: '64748B',
                }),
              ],
            })
          ],
          width: { size: 70, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
        }),
      ],
    }),
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: '⏱️ Durée',
                  bold: true,
                  size: 20,
                  font: fallbackFont,
                  color: '059669',
                }),
              ],
            })
          ],
          width: { size: 30, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
          shading: { fill: 'F0FDF4' },
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: durationText,
                  size: 20,
                  font: fallbackFont,
                  color: '64748B',
                }),
              ],
            })
          ],
          width: { size: 70, type: WidthType.PERCENTAGE },
          margins: { top: 200, bottom: 200, left: 300, right: 300 },
        }),
      ],
    }),
  ];

  // Ajouter les participants si disponibles
  if (meeting.participants && meeting.participants.length > 0) {
    const participantsText = meeting.participants
      .map(p => typeof p === 'string' ? p : p.displayName || p.name || 'Participant')
      .join(', ');
    
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '👥 Participants',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: '059669',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F0FDF4' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: participantsText,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter le nombre de segments de transcription
  if (transcription?.utterances?.length > 0) {
    infoRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '🎯 Segments',
                    bold: true,
                    size: 20,
                    font: fallbackFont,
                    color: '059669',
                  }),
                ],
              })
            ],
            width: { size: 30, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
            shading: { fill: 'F0FDF4' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${transcription.utterances.length} interventions`,
                    size: 20,
                    font: fallbackFont,
                    color: '64748B',
                  }),
                ],
              })
            ],
            width: { size: 70, type: WidthType.PERCENTAGE },
            margins: { top: 200, bottom: 200, left: 300, right: 300 },
          }),
        ],
      })
    );
  }

  // Ajouter le tableau d'informations
  docChildren.push(
    new Table({
      rows: infoRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: 'single', size: 2, color: '059669' },
        bottom: { style: 'single', size: 2, color: '059669' },
        left: { style: 'single', size: 2, color: '059669' },
        right: { style: 'single', size: 2, color: '059669' },
        insideHorizontal: { style: 'single', size: 1, color: 'D1FAE5' },
        insideVertical: { style: 'single', size: 1, color: 'D1FAE5' },
      },
    })
  );

  // Espacement avant le contenu
  docChildren.push(
    new Paragraph({
      spacing: { before: 600, after: 400 },
      children: [
        new TextRun({
          text: '',
        }),
      ],
    })
  );

  // Titre de section transcription
  docChildren.push(
    new Paragraph({
      spacing: { before: 300, after: 300 },
      children: [
        new TextRun({
          text: '🎤 ',
          size: 28,
          font: fallbackFont,
        }),
        new TextRun({
          text: 'TRANSCRIPTION INTÉGRALE',
          bold: true,
          size: 24,
          font: fallbackFont,
          color: '1E293B',
          allCaps: true,
        }),
      ],
    })
  );

  // Ligne sous le titre
  docChildren.push(
    new Paragraph({
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: '▔'.repeat(50),
          size: 16,
          font: fallbackFont,
          color: '059669',
        }),
      ],
    })
  );

  // Contenu de la transcription
  if (transcription) {
    if (transcription.utterances && Array.isArray(transcription.utterances)) {
      // Fonction pour formater le timestamp
      const formatTimestamp = (ms) => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        
        if (hours > 0) {
          return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        }
        return `${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
      };

      // Couleurs pour les différents locuteurs
      const speakerColors = ['3B82F6', 'EF4444', '10B981', 'F59E0B', '8B5CF6', 'EC4899', '06B6D4', 'F97316'];

      transcription.utterances.forEach((utterance, index) => {
        const speaker = utterance.speaker || 'Locuteur';
        const text = utterance.text || '';
        const timestamp = utterance.start ? formatTimestamp(utterance.start) : '';
        
        // Choisir une couleur basée sur le speaker
        const colorIndex = speaker.charCodeAt(speaker.length - 1) % speakerColors.length;
        const speakerColor = speakerColors[colorIndex];
        
        // Espacement entre les interventions
        if (index > 0) {
          docChildren.push(
            new Paragraph({
              spacing: { before: 200 },
              children: [
                new TextRun({
                  text: '',
                }),
              ],
            })
          );
        }

        // Ligne d'en-tête avec locuteur et timestamp
        docChildren.push(
          new Paragraph({
            spacing: { before: index === 0 ? 0 : 300, after: 100 },
            shading: { fill: 'F8FAFC' },
            border: {
              left: { style: 'single', size: 8, color: speakerColor, space: 100 },
            },
            children: [
              new TextRun({
                text: '🗣️ ',
                size: 18,
                font: fallbackFont,
              }),
              new TextRun({
                text: speaker,
                bold: true,
                size: 18,
                font: fallbackFont,
                color: speakerColor,
              }),
              ...(timestamp ? [
                new TextRun({
                  text: ` • ${timestamp}`,
                  size: 16,
                  font: fallbackFont,
                  color: '64748B',
                  italics: true,
                }),
              ] : []),
            ],
          })
        );
        
        // Texte de l'intervention avec indentation
        docChildren.push(
          new Paragraph({
            spacing: { after: 150 },
            indent: { left: 720 },
            children: [
              new TextRun({
                text: text,
                size: 18,
                font: fallbackFont,
                color: '374151',
                
              }),
            ],
          })
        );
      });
    } else if (transcription.fullText) {
      // Traiter le texte plat
      const lines = transcription.fullText.split(/\n+/).filter(line => line.trim());
      lines.forEach((line, index) => {
        // Détecter si c'est une ligne de locuteur (contient ":")
        if (line.includes(':') && !line.startsWith(' ')) {
          const [speaker, ...textParts] = line.split(':');
          const text = textParts.join(':').trim();
          
          docChildren.push(
            new Paragraph({
              spacing: { before: index > 0 ? 250 : 0, after: 100 },
              children: [
                new TextRun({
                  text: '🗣️ ',
                  size: 18,
                  font: fallbackFont,
                }),
                new TextRun({
                  text: speaker.trim(),
                  bold: true,
                  size: 18,
                  font: fallbackFont,
                  color: '3B82F6',
                }),
              ],
            })
          );
          
          if (text) {
            docChildren.push(
              new Paragraph({
                spacing: { after: 150 },
                indent: { left: 720 },
                children: [
                  new TextRun({
                    text: text,
                    size: 18,
                    font: fallbackFont,
                    color: '374151',
                  }),
                ],
              })
            );
          }
        } else {
          // Ligne de texte simple
          docChildren.push(
            new Paragraph({
              spacing: { after: 120 },
              children: [
                new TextRun({
                  text: line,
                  size: 18,
                  font: fallbackFont,
                  color: '374151',
                }),
              ],
            })
          );
        }
      });
    } else {
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: 'Aucune transcription disponible.',
              italics: true,
              size: 18,
              font: fallbackFont,
              color: '94A3B8',
            }),
          ],
        })
      );
    }
  }

  // Espacement avant le pied de page
  docChildren.push(
    new Paragraph({
      spacing: { before: 800 },
      children: [
        new TextRun({
          text: '',
        }),
      ],
    })
  );

  // Ligne de séparation avant pied de page
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { before: 400, after: 200 },
      children: [
        new TextRun({
          text: '◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆ ◇ ◆',
          size: 14,
          font: fallbackFont,
          color: 'CBD5E1',
        }),
      ],
    })
  );

  // Pied de page élégant
  docChildren.push(
    new Paragraph({
      alignment: 'center',
      spacing: { before: 200 },
      children: [
        new TextRun({
          text: '🤖 Document généré automatiquement par l\'IA de Transcription le ',
          size: 16,
          italics: true,
          font: fallbackFont,
          color: '64748B',
        }),
        new TextRun({
          text: format(new Date(), "d MMMM yyyy 'à' HH:mm", { locale: fr }),
          size: 16,
          italics: true,
          bold: true,
          font: fallbackFont,
          color: '059669',
        }),
      ],
    })
  );

  return new Document({
    styles: {
      default: {
        document: {
          run: {
            font: fallbackFont,
            size: 18,
            color: '374151',
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        children: docChildren,
      },
    ],
  });
}

// Fonction utilitaire pour convertir HTML en paragraphes Word modernes
function htmlToDocxParagraphsModern(html, font = 'Segoe UI, Roboto, Arial, sans-serif') {
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements = Array.from(doc.body.childNodes);
  const paragraphs = [];

  function parseNode(node) {
    if (node.nodeType === 3) { // text
      return [new Paragraph({ 
        children: [new TextRun({ 
          text: node.textContent, 
          font, 
          size: 18, 
          color: '475569' 
        })] 
      })];
    }
    if (node.nodeType !== 1) return [];
    
    const tag = node.tagName.toLowerCase();
    switch (tag) {
      case 'h1':
        return [new Paragraph({ 
          children: [new TextRun({ 
            text: node.textContent, 
            bold: true, 
            font, 
            size: 24, 
            color: '1E293B' 
          })],
          spacing: { before: 400, after: 200 }
        })];
      case 'h2':
        return [new Paragraph({ 
          children: [new TextRun({ 
            text: node.textContent, 
            bold: true, 
            font, 
            size: 22, 
            color: '374151' 
          })],
          spacing: { before: 300, after: 150 }
        })];
      case 'h3':
        return [new Paragraph({ 
          children: [new TextRun({ 
            text: node.textContent, 
            bold: true, 
            font, 
            size: 20, 
            color: '475569' 
          })],
          spacing: { before: 250, after: 100 }
        })];
      case 'ul':
        return Array.from(node.children).map(li =>
          new Paragraph({
            children: [
              new TextRun({ text: '• ', font, size: 18, color: '6366F1', bold: true }),
              new TextRun({ text: li.textContent, font, size: 18, color: '475569' })
            ],
            indent: { left: 360 },
            spacing: { after: 100 }
          })
        );
      case 'ol':
        return Array.from(node.children).map((li, i) =>
          new Paragraph({
            children: [
              new TextRun({ text: `${i + 1}. `, bold: true, font, size: 18, color: '6366F1' }),
              new TextRun({ text: li.textContent, font, size: 18, color: '475569' })
            ],
            indent: { left: 360 },
            spacing: { after: 100 }
          })
        );
      case 'p':
        return [new Paragraph({ 
          children: parseInline(node, font),
          spacing: { after: 150 }
        })];
      case 'table':
        return parseTableModern(node, font);
      case 'blockquote':
        return [new Paragraph({
          children: [new TextRun({ 
            text: `"${node.textContent}"`, 
            italics: true, 
            color: '64748B', 
            font, 
            size: 18 
          })],
          indent: { left: 720 },
          spacing: { after: 150 },
          border: {
            left: { style: 'single', size: 4, color: '6366F1', space: 100 },
          }
        })];
      case 'pre':
        return [new Paragraph({
          children: [new TextRun({ 
            text: node.textContent, 
            font: 'Consolas, monospace', 
            color: '1F2937', 
            size: 16 
          })],
          spacing: { after: 150 },
          shading: { fill: 'F9FAFB' }
        })];
      case 'hr':
        return [new Paragraph({ 
          children: [new TextRun({ 
            text: '▔'.repeat(60), 
            font, 
            size: 16, 
            color: 'E5E7EB' 
          })],
          alignment: 'center',
          spacing: { before: 200, after: 200 }
        })];
      default:
        return [new Paragraph({ children: parseInline(node, font) })];
    }
  }

  function parseTableModern(tableNode, font) {
    try {
      const rows = Array.from(tableNode.querySelectorAll('tr'));
      if (rows.length === 0) return [];

      const tableRows = rows.map((row, rowIndex) => {
        const cells = Array.from(row.querySelectorAll('td, th'));
        const tableCells = cells.map(cell => {
          const isHeader = cell.tagName.toLowerCase() === 'th' || rowIndex === 0;
          return new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: cell.textContent || '',
                font,
                size: 16,
                bold: isHeader,
                color: isHeader ? '1E293B' : '475569'
              })],
              spacing: { before: 100, after: 100 }
            })],
            width: { size: 100 / cells.length, type: WidthType.PERCENTAGE },
            margins: { top: 150, bottom: 150, left: 200, right: 200 },
            shading: isHeader ? { fill: 'F8FAFC' } : undefined
          });
        });

        return new TableRow({
          children: tableCells
        });
      });

      return [
        new Paragraph({ text: '', spacing: { before: 200 } }),
        new Table({
          rows: tableRows,
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: 'single', size: 2, color: '6366F1' },
            bottom: { style: 'single', size: 2, color: '6366F1' },
            left: { style: 'single', size: 2, color: '6366F1' },
            right: { style: 'single', size: 2, color: '6366F1' },
            insideHorizontal: { style: 'single', size: 1, color: 'E5E7EB' },
            insideVertical: { style: 'single', size: 1, color: 'E5E7EB' }
          }
        }),
        new Paragraph({ text: '', spacing: { after: 200 } })
      ];
    } catch (error) {

      return [new Paragraph({ 
        children: [new TextRun({ 
          text: '[Tableau non pris en charge]', 
          italics: true, 
          color: '94A3B8',
          font
        })] 
      })];
    }
  }

  function parseInline(node, font) {
    const runs = [];
    node.childNodes.forEach(child => {
      if (child.nodeType === 3) {
        runs.push(new TextRun({ text: child.textContent, font, size: 18, color: '475569' }));
      } else if (child.nodeType === 1) {
        const tag = child.tagName.toLowerCase();
        switch (tag) {
          case 'strong':
          case 'b':
            runs.push(new TextRun({ text: child.textContent, bold: true, font, size: 18, color: '1E293B' }));
            break;
          case 'em':
          case 'i':
            runs.push(new TextRun({ text: child.textContent, italics: true, font, size: 18, color: '475569' }));
            break;
          case 'u':
            runs.push(new TextRun({ text: child.textContent, underline: {}, font, size: 18, color: '475569' }));
            break;
          case 'a':
            runs.push(new TextRun({ text: child.textContent, color: '3B82F6', underline: {}, font, size: 18 }));
            break;
          case 'code':
            runs.push(new TextRun({ text: child.textContent, font: 'Consolas, monospace', color: 'DC2626', size: 16 }));
            break;
          default:
            runs.push(new TextRun({ text: child.textContent, font, size: 18, color: '475569' }));
        }
      }
    });
    return runs;
  }

  elements.forEach(el => {
    const result = parseNode(el);
    result.forEach(par => {
      if (par instanceof Paragraph || par instanceof Table) {
        paragraphs.push(par);
      }
    });
  });
  
  return paragraphs;
}

const MeetingsListPage = () => {
  const navigate = useNavigate();
  const { setLoading, setError } = useApp();
  const { currentUser, loading: authLoading } = useAuth();
  const { addDeleteNotification, addActionErrorNotification, addSystemNotification, addExportNotification } = useNotifications();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [meetings, setMeetings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [tabValue, setTabValue] = useState('user');
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [transcriptEmailDialogOpen, setTranscriptEmailDialogOpen] = useState(false);
  const [selectedMeetingForEmail, setSelectedMeetingForEmail] = useState(null);
  const [selectedSummaryForEmail, setSelectedSummaryForEmail] = useState(null);
  const [selectedTranscriptForEmail, setSelectedTranscriptForEmail] = useState(null);
  const [newMeetingDialogOpen, setNewMeetingDialogOpen] = useState(false);
  const [editMeetingDialogOpen, setEditMeetingDialogOpen] = useState(false);
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  const [infoExpanded, setInfoExpanded] = useState(false);

  // États pour les données temps réel
  const [transcriptions, setTranscriptions] = useState({});
  const [summaries, setSummaries] = useState({});
  
  // Références pour gérer les listeners
  const transcriptionListenersRef = useRef({});
  const summaryListenersRef = useRef({});

  // Références pour éviter les re-initialisations multiples (copié de HomePage.js)
  const initializationRef = useRef(false);
  const userKey = useRef('');
  const unsubscribersRef = useRef({});

  // Détection admin
  const isAdmin = currentUser && (currentUser.profile?.isAdmin || currentUser.isAdmin);

  // Charger la liste des utilisateurs si admin
  useEffect(() => {
    if (!isAdmin) return;
    setIsLoadingUsers(true);
    userService.searchUsers('').then((usersList) => {
      setUsers(usersList);
      setIsLoadingUsers(false);
    }).catch(() => setIsLoadingUsers(false));
  }, [isAdmin]);

  // Écoute temps réel des réunions
  useEffect(() => {
    const currentKey = `${currentUser?.uid || ''}_${isAdmin}_${tabValue}_${selectedUser?.id || 'none'}`;

    // Si l'utilisateur/config change, réinitialiser
    if (userKey.current !== currentKey) {
      userKey.current = currentKey;
      initializationRef.current = false;
      
      // Nettoyer les anciens listeners
      Object.values(unsubscribersRef.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
      unsubscribersRef.current = {};
    }
    
    let isMounted = true; // Flag pour éviter les setState après unmount
    
    const loadData = async () => {
      if (!currentUser?.uid || initializationRef.current || !isMounted) {
        return;
      }
      
             initializationRef.current = true; // Marquer comme en cours d'initialisation
       
       try {
         if (isMounted) setIsLoadingMeetings(true);
    
    // Configurer l'écoute temps réel des réunions
    const handleMeetingsUpdate = (meetings) => {
          if (!isMounted) return;
          
      // Pour les admins avec tabValue 'all', on doit encore filtrer selon le selectedUser
      let finalMeetings = meetings;
      if (isAdmin && tabValue === 'all' && selectedUser && selectedUser.id) {
        finalMeetings = meetings.filter(m => m.userId === selectedUser.id);
      }
      
      // SÉCURITÉ : Filtrer les réunions confidentielles pour les admins (ne devraient pas apparaître mais par sécurité)
      const currentUserId = getCurrentUserId();
      finalMeetings = finalMeetings.filter(meeting => {
        // Si c'est une réunion confidentielle et qu'on n'est pas le propriétaire, on la cache
        if (meeting.isConfidential && meeting.userId !== currentUserId) {
          return false;
        }
        return true;
      });
      
      setMeetings(finalMeetings);
      setIsLoadingMeetings(false);
      
      // Nettoyer les listeners existants pour transcriptions/résumés
      Object.values(transcriptionListenersRef.current).forEach(unsubscribe => unsubscribe());
      Object.values(summaryListenersRef.current).forEach(unsubscribe => unsubscribe());
      transcriptionListenersRef.current = {};
      summaryListenersRef.current = {};
      setTranscriptions({});
      setSummaries({});
      
      // Configurer l'écoute temps réel pour les transcriptions et résumés
      finalMeetings.forEach(meeting => {
        if (meeting.transcriptionId) {
          // Écouter la transcription
          const unsubscribeTranscription = transcriptionService.onTranscriptionById(
            meeting.transcriptionId,
            (transcription) => {
                  if (!isMounted) return;
              setTranscriptions(prev => ({
                ...prev,
                [meeting.id]: transcription
              }));
            }
          );
          transcriptionListenersRef.current[meeting.id] = unsubscribeTranscription;
          
          // Écouter le résumé
          const unsubscribeSummary = summaryService.onSummaryByTranscriptionId(
            meeting.transcriptionId,
            (summary) => {
                  if (!isMounted) return;
              setSummaries(prev => ({
                ...prev,
                [meeting.id]: summary
              }));
            },
            { isAdmin }
          );
          summaryListenersRef.current[meeting.id] = unsubscribeSummary;
        }
      });
    };
    
    // CORRECTION : Utiliser le bon listener selon l'onglet actif
    let unsubscribeMeetings;
    const userId = getCurrentUserId();
    
    if (isAdmin && tabValue === 'all') {
      // Admin : toutes les réunions
      unsubscribeMeetings = meetingService.onAllMeetings(handleMeetingsUpdate);
    } else if (tabValue === 'shared') {
      // Réunions partagées avec l'utilisateur
      unsubscribeMeetings = meetingService.onSharedMeetings(userId, handleMeetingsUpdate);
    } else {
      // Réunions de l'utilisateur (onglet 'user')
      unsubscribeMeetings = meetingService.onUserMeetings(userId, handleMeetingsUpdate);
    }
    
        // Stocker le listener pour le nettoyage
        unsubscribersRef.current.meetings = unsubscribeMeetings;
        
      } catch (error) {

        if (isMounted) {
          setIsLoadingMeetings(false);
        }
      }
    };

    if (currentUser?.uid && !initializationRef.current) {
      // Délai pour éviter les appels multiples
      const timeoutId = setTimeout(() => {
        if (!initializationRef.current) {
          loadData();
        }
      }, 100);
      
    return () => {
        clearTimeout(timeoutId);
        isMounted = false;
      };
    }
    
    // Cleanup function
    return () => {
      isMounted = false;
      // Les listeners seront nettoyés lors du changement d'utilisateur ou unmount du composant
    };
  }, [currentUser?.uid, isAdmin, tabValue, selectedUser?.id]); // Dépendances complètes pour détecter tous les changements
  
  // Filtrer les réunions selon le terme de recherche et les mots-clés sélectionnés
  const filteredMeetings = meetings.filter(meeting => {
    // Filtre par terme de recherche
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch = (
        (meeting.title && meeting.title.toLowerCase().includes(searchTermLower)) ||
        (meeting.context && meeting.context.toLowerCase().includes(searchTermLower)) ||
        (meeting.participants && (
          typeof meeting.participants === 'string'
            ? meeting.participants.toLowerCase().includes(searchTermLower)
            : JSON.stringify(meeting.participants).toLowerCase().includes(searchTermLower)
        ))
      );
      if (!matchesSearch) return false;
    }

    // Filtre par mots-clés
    if (selectedKeywords.length > 0) {
      const meetingSummary = summaries[meeting.id];
      if (!meetingSummary || !meetingSummary.keywords) return false;

      // La réunion doit avoir au moins un des mots-clés sélectionnés
      const hasSelectedKeyword = selectedKeywords.some(selectedKeyword =>
        meetingSummary.keywords.includes(selectedKeyword)
      );
      if (!hasSelectedKeyword) return false;
    }

    return true;
  });
  
  // Trier les réunions par date (les plus récentes d'abord)
  const sortedMeetings = [...filteredMeetings].sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  );
  
  // Gérer la suppression d'une réunion
  const handleDeleteMeeting = async (id) => {
    setLoading(true);
    try {
      await meetingService.deleteMeeting(id);
      // La réunion disparaîtra automatiquement de la liste grâce au listener temps réel
    } catch (error) {
      setError('Erreur lors de la suppression de la réunion');

    } finally {
      setLoading(false);
    }
  };
  
  // Ouvrir le dialog de création
  const handleAddMeeting = () => {
    setNewMeetingDialogOpen(true);
  };

  // Callback de succès de création de réunion
  const handleNewMeetingSuccess = (meetingId) => {
    setNewMeetingDialogOpen(false);
    // La nouvelle réunion apparaîtra automatiquement grâce au listener temps réel
    // Optionnel : naviguer vers la réunion créée
    // navigate(`/meetings/${meetingId}`);
  };

  // Fonction pour ouvrir le dialog d'édition
  const handleEditMeeting = (meetingId) => {
    setEditingMeetingId(meetingId);
    setEditMeetingDialogOpen(true);
  };

  // Callback de succès d'édition de réunion
  const handleEditMeetingSuccess = (meetingId) => {
    setEditMeetingDialogOpen(false);
    setEditingMeetingId(null);
    // La réunion sera mise à jour automatiquement grâce au listener temps réel
  };

  // Fonction pour fermer le dialog d'édition
  const handleCloseEditMeetingDialog = () => {
    setEditMeetingDialogOpen(false);
    setEditingMeetingId(null);
  };

  const handleExportWord = async (meeting) => {
    setLoading(true);
    try {
      let summary = summaries[meeting.id];
      if (!summary && meeting.transcriptionId) {
        summary = await summaryService.getSummaryByTranscriptionId(
          meeting.transcriptionId, 
          { isAdmin }
        );
      }

      if (!summary || !summary.text) {
        setError('Aucun résumé disponible pour cette réunion');
        return;
      }

      // Fonction pour formater la date au format jjMMAAA
      const formatDateForFilename = (date) => {
        if (!date) return 'SansDate';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'SansDate';
        
        const day = d.getDate().toString().padStart(2, '0');
        const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        
        return `${day}${month}${year}`;
      };

      // Nettoyer le titre pour le nom de fichier
      const cleanTitle = (meeting.title || 'Sans-titre')
        .replace(/[<>:"/\\|?*]/g, '-')
        .replace(/\s+/g, '-')
        .substring(0, 50);

      const doc = await createSummaryWordDocument(meeting, summary);
      const blob = await Packer.toBlob(doc);
      const dateFormatted = formatDateForFilename(meeting.date);
      const fileName = `${dateFormatted}-Réunion-${cleanTitle}-Résumé.docx`;
      saveAs(blob, fileName);
      await addExportNotification(fileName, 'Word');
    } catch (error) {
      setError('Erreur lors de la génération du document Word');

    } finally {
      setLoading(false);
    }
  };

  const handleExportTranscriptWord = async (meeting) => {
    setLoading(true);
    try {
      let transcription = transcriptions[meeting.id];
      if (!transcription && meeting.transcriptionId) {
        transcription = await transcriptionService.getTranscriptionById(meeting.transcriptionId);
      }

      if (!transcription) {
        setError('Aucune transcription disponible pour cette réunion');
        return;
      }

      // Fonction pour formater la date au format jjMMAAA
      const formatDateForFilename = (date) => {
        if (!date) return 'SansDate';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'SansDate';
        
        const day = d.getDate().toString().padStart(2, '0');
        const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        
        return `${day}${month}${year}`;
      };

      // Nettoyer le titre pour le nom de fichier
      const cleanTitle = (meeting.title || 'Sans-titre')
        .replace(/[<>:"/\\|?*]/g, '-')
        .replace(/\s+/g, '-')
        .substring(0, 50);

      const doc = await createTranscriptWordDocument(meeting, transcription);
      const blob = await Packer.toBlob(doc);
      const dateFormatted = formatDateForFilename(meeting.date);
      const fileName = `${dateFormatted}-Réunion-${cleanTitle}-Transcription.docx`;
      saveAs(blob, fileName);
      await addExportNotification(fileName, 'Word');
    } catch (error) {
      setError('Erreur lors de la génération du document Word');

    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ouvrir le dialog d'email pour le résumé
  const handleOpenEmailDialog = async (meeting) => {
    let summary = summaries[meeting.id];
    if (!summary && meeting.transcriptionId) {
      try {
        summary = await summaryService.getSummaryByTranscriptionId(
          meeting.transcriptionId, 
          { isAdmin }
        );
        setSelectedSummaryForEmail(summary);
      } catch (error) {

        setError('Impossible de charger le résumé pour cet email');
        return;
      }
    } else {
      setSelectedSummaryForEmail(summary);
    }
    
    setSelectedMeetingForEmail(meeting);
    setEmailDialogOpen(true);
  };

  // Fonction pour ouvrir le dialog d'email pour la transcription
  const handleOpenTranscriptEmailDialog = async (meeting) => {
    let transcription = transcriptions[meeting.id];
    if (!transcription && meeting.transcriptionId) {
      try {
        transcription = await transcriptionService.getTranscriptionById(meeting.transcriptionId);
        setSelectedTranscriptForEmail(transcription);
      } catch (error) {

        setError('Impossible de charger la transcription pour cet email');
        return;
      }
    } else {
      setSelectedTranscriptForEmail(transcription);
    }
    
    setSelectedMeetingForEmail(meeting);
    setTranscriptEmailDialogOpen(true);
  };

  // Fonction pour fermer le dialog d'email résumé
  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedMeetingForEmail(null);
    setSelectedSummaryForEmail(null);
  };

  // Fonction pour fermer le dialog d'email transcription
  const handleCloseTranscriptEmailDialog = () => {
    setTranscriptEmailDialogOpen(false);
    setSelectedMeetingForEmail(null);
    setSelectedTranscriptForEmail(null);
  };

  // Écoute temps réel des projets (uniquement si on est sur l'onglet projets)
  useEffect(() => {
    if (tabValue !== 'projects' || !currentUser?.uid) return;

    setIsLoadingProjects(true);
    const unsubscribe = projectService.onUserProjects(currentUser.uid, (userProjects) => {
      setProjects(userProjects);
      setIsLoadingProjects(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [tabValue, currentUser?.uid]);

  // Extraire tous les mots-clés uniques des résumés
  const allKeywords = React.useMemo(() => {
    const keywordsSet = new Set();
    Object.values(summaries).forEach(summary => {
      if (summary && summary.keywords && Array.isArray(summary.keywords)) {
        summary.keywords.forEach(keyword => keywordsSet.add(keyword));
      }
    });
    return Array.from(keywordsSet).sort();
  }, [summaries]);

  // Cleanup global à l'unmount du composant (copié de HomePage.js)
  useEffect(() => {
    return () => {
      Object.values(unsubscribersRef.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
      unsubscribersRef.current = {};
    };
  }, []);

  // Nettoyage du cache audio global à l'arrivée sur la liste des réunions
  useEffect(() => {
    if (window.globalSpeakerSamplesCache && window.globalSpeakerSamplesCache.current) {
      window.globalSpeakerSamplesCache.current = {};
    }
  }, []);

  // Sécurité : afficher un loader tant que l'auth n'est pas prête
  if (authLoading || !currentUser) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box
      // component="main"
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        py: 4,
        px: 2,
        pb: isXs ? 10 : 4, // Plus de padding en bas sur mobile pour le bottom menu
        // overflowX: 'hidden'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ flex: 1, mr: { xs: 0, sm: 4 } }}>
          {/* Description élégante de la page */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #f8faff 0%, #e8f4fd 100%)',
            borderRadius: 3,
            p: 3,
            border: '1px solid #e8f4fd',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
              borderRadius: '50%',
              opacity: 0.1,
              transform: 'translate(20px, -20px)'
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isMobile && !infoExpanded ? 0 : 1.5 }}>
                <Typography 
                  variant="h5" 
                  fontWeight={700}
                  sx={{ 
                    color: '#2c3e50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}
                >
                  🎙️ Vos réunions transcrites
                </Typography>
                {isMobile && (
                  <IconButton 
                    size="small" 
                    onClick={() => setInfoExpanded(!infoExpanded)}
                    sx={{ 
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                      transform: infoExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {(!isMobile || infoExpanded) && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#546e7a',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    maxWidth: '600px'
                  }}
                >
                  Importez vos fichiers audio de réunions pour obtenir une <strong>transcription automatique</strong> avec identification des intervenants, puis générez des <strong>résumés intelligents</strong> personnalisés selon vos besoins.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
          onClick={handleAddMeeting}
          sx={{
            display: { xs: 'none', sm: 'flex' }, // Cacher sur mobile
            px: { xs: 2, sm: 3 },
            py: { xs: 0.75, sm: 1 },
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            fontWeight: 500,
            borderRadius: { xs: '20px', sm: '8px' },
            textTransform: 'none',
            minWidth: { xs: 'auto', sm: 'auto' },
            background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
            boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 16px rgba(63, 81, 181, 0.4)'
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiButton-startIcon': {
              marginRight: { xs: 0.5, sm: 1 }
            }
          }}
        >
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Nouvelle réunion</Box>
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Nouveau</Box>
        </Button>
      </Box>

      {/* Onglets pour desktop */}
      {!isXs && (
        <Paper elevation={0} sx={{ 
          mb: 3, 
          borderRadius: 2, 
          border: '1px solid', 
          borderColor: 'divider',
          overflow: 'hidden'
        }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                minHeight: 56,
                py: 2
              }
            }}
          >
            <Tab 
              icon={<MicIcon />} 
              iconPosition="start"
              label="Mes réunions" 
              value="user"
              sx={{ gap: 1 }}
            />
            <Tab
              icon={<ShareIcon />}
              iconPosition="start"
              label="Partagées avec moi"
              value="shared"
              sx={{ gap: 1 }}
            />
            {isAdmin && (
              <Tab
                icon={<FolderIcon />}
                iconPosition="start"
                label="Projets"
                value="projects"
                sx={{ gap: 1 }}
              />
            )}
            {isAdmin && (
              <Tab
                icon={<PeopleAltIcon />}
                iconPosition="start"
                label="Toutes les réunions"
                value="all"
                sx={{ gap: 1 }}
              />
            )}
          </Tabs>
        </Paper>
      )}

      {/* Sélecteur d'utilisateur pour admin */}
      {isAdmin && tabValue === 'all' && (
        <Paper elevation={0} sx={{ 
          mb: 3, 
          p: 2, 
          borderRadius: 2, 
          border: '1px solid', 
          borderColor: 'divider'
        }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 500, 
            mr: 2, 
            mb: 2
          }}>
            Filtrer par utilisateur :
          </Typography>
          <TextField
            select
            label="Utilisateur"
            value={selectedUser ? selectedUser.id : ''}
            onChange={e => {
              const user = users.find(u => u.id === e.target.value);
              setSelectedUser(user || null);
            }}
            sx={{ 
              minWidth: 220
            }}
            disabled={isLoadingUsers}
            size="small"
          >
            <MenuItem value="">Tous les utilisateurs</MenuItem>
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {user.displayName || user.email || user.id}
              </MenuItem>
            ))}
          </TextField>
        </Paper>
      )}

      {/* Filtres par mots-clés (seulement pour les onglets de réunions) */}
      {tabValue !== 'projects' && allKeywords.length > 0 && (
        <Paper elevation={0} sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LocalOfferIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={500}>
              Filtrer par mots-clés:
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {allKeywords.map(keyword => (
              <Chip
                key={keyword}
                label={keyword}
                size="small"
                color={selectedKeywords.includes(keyword) ? 'primary' : 'default'}
                onClick={() => {
                  setSelectedKeywords(prev =>
                    prev.includes(keyword)
                      ? prev.filter(k => k !== keyword)
                      : [...prev, keyword]
                  );
                }}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 1
                  }
                }}
              />
            ))}
          </Stack>
          {selectedKeywords.length > 0 && (
            <Button
              size="small"
              onClick={() => setSelectedKeywords([])}
              sx={{ mt: 1 }}
            >
              Effacer les filtres
            </Button>
          )}
        </Paper>
      )}

      {/* Recherche */}
      <Paper elevation={0} sx={{
        mb: 4,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <TextField
          fullWidth
          placeholder={tabValue === 'projects' ? "Rechercher un projet..." : "Rechercher une réunion..."}
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              px: 2,
              py: 0.5,
              '& fieldset': { border: 'none' }
            }
          }}
        />
      </Paper>

      {/* Affichage conditionnel : Projets ou Réunions */}
      {tabValue === 'projects' && isAdmin ? (
        // Vue Projets
        isLoadingProjects ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : projects.length === 0 ? (
          <Paper elevation={0} sx={{ my: 8, textAlign: 'center', py: 6, px: 3, borderRadius: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', border: '1px dashed', borderColor: 'divider' }}>
            <Box>
              <FolderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                Vous n'avez pas encore de projets
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
                Créez un projet pour organiser vos réunions par thématique
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/projects')}
                sx={{ mt: 2 }}
              >
                Voir mes projets
              </Button>
            </Box>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {projects.filter(project => {
              if (!searchTerm) return true;
              const searchLower = searchTerm.toLowerCase();
              return (
                (project.name && project.name.toLowerCase().includes(searchLower)) ||
                (project.description && project.description.toLowerCase().includes(searchLower))
              );
            }).map(project => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      borderColor: project.color || 'primary.main'
                    }
                  }}
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: project.color || '#1976d2',
                        flexShrink: 0
                      }}
                    />
                    <Typography variant="h6" fontWeight="bold" noWrap sx={{ flex: 1 }}>
                      {project.name}
                    </Typography>
                  </Box>
                  {project.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {project.description}
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    icon={<FolderIcon fontSize="small" />}
                    label={`${project.meetingIds?.length || 0} réunion${(project.meetingIds?.length || 0) > 1 ? 's' : ''}`}
                    color="primary"
                    variant="outlined"
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        )
      ) : (
        // Vue Réunions
        isLoadingMeetings ? (
          <Grid container spacing={2}>
            {[...Array(6)].map((_, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <UniversalCard
                  type="meeting"
                  isLoading={true}
                />
              </Grid>
            ))}
          </Grid>
        ) : sortedMeetings.length > 0 ? (
        <Grid container spacing={2}>
          {sortedMeetings.map(meeting => (
            <Grid item xs={12} sm={6} md={4} key={meeting.id}>
              <UniversalCard 
                type="meeting"
                item={meeting}
                transcription={transcriptions[meeting.id]}
                summary={summaries[meeting.id]}
                tabValue={tabValue}
                actions={[
                  {
                    type: 'view',
                    icon: <VisibilityIcon fontSize="small" />,
                    tooltip: 'Afficher la réunion',
                    color: 'info'
                  },
                  {
                    type: 'edit',
                    icon: <EditIcon fontSize="small" />,
                    tooltip: 'Modifier',
                    color: 'primary'
                  }
                ]}
                menuItems={[
                  {
                    action: 'edit',
                    label: 'Modifier',
                    icon: <EditIcon />,
                    color: '#1976d2'
                  },
                  ...(transcriptions[meeting.id] ? [
                    {
                      action: 'exportTranscriptWord',
                      label: 'Exporter transcription Word',
                      icon: <DownloadIcon />,
                      color: '#2196f3'
                    },
                    ...(summaries[meeting.id] ? [
                      {
                        action: 'exportWord',
                        label: 'Exporter résumé Word',
                        icon: <DownloadIcon />,
                        color: '#4caf50'
                      }
                    ] : []),
                    {
                      action: 'emailTranscript',
                      label: 'Envoyer transcription par email',
                      icon: <EmailIcon />,
                      color: '#9c27b0'
                    },
                    ...(summaries[meeting.id] ? [
                      {
                        action: 'emailSummary',
                        label: 'Envoyer résumé par email',
                        icon: <EmailIcon />,
                        color: '#ff9800'
                      }
                    ] : [
                      {
                        action: 'generateSummary',
                        label: 'Générer un résumé',
                        icon: <DescriptionIcon />,
                        color: '#9c27b0'
                      }
                    ])
                  ] : []),

                  {
                    action: 'delete',
                    label: 'Supprimer',
                    icon: <DeleteIcon />,
                    color: '#f44336'
                  }
                ]}
                onAction={(action, item, actionData) => {
                  switch (action) {
                    case 'view':
                      navigate(`/meetings/${item.id}`);
                      break;
                    case 'edit':
                      handleEditMeeting(item.id);
                      break;
                    case 'delete':
                      handleDeleteMeeting(item.id);
                      break;
                    case 'exportWord':
                      handleExportWord(item);
                      break;
                    case 'exportTranscriptWord':
                      handleExportTranscriptWord(item);
                      break;
                    case 'emailSummary':
                      handleOpenEmailDialog(item);
                      break;
                    case 'emailTranscript':
                      handleOpenTranscriptEmailDialog(item);
                      break;
                    case 'generateSummary':
                      // TODO: implémenter
                      break;

                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper elevation={0} sx={{ my: 8, textAlign: 'center', py: 6, px: 3, borderRadius: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', border: '1px dashed', borderColor: 'divider' }}>
          {searchTerm ? (
            <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto', alignItems: 'center' }}>
              <Typography align="center">
                Aucune réunion ne correspond à votre recherche "{searchTerm}"
              </Typography>
            </Alert>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                Vous n'avez pas encore de réunions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
                Commencez par créer une nouvelle réunion pour transcrire un fichier audio
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddMeeting}
                sx={{ mt: 2 }}
              >
                Créer ma première réunion
              </Button>
            </Box>
          )}
        </Paper>
        )
      )}

      {/* Dialog d'envoi d'email pour le résumé */}
      <EmailDialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        meeting={selectedMeetingForEmail}
        summary={selectedSummaryForEmail}
        contentType="summary"
        title="Envoyer le résumé par email"
      />

      {/* Dialog d'envoi d'email pour la transcription */}
      <EmailDialog
        open={transcriptEmailDialogOpen}
        onClose={handleCloseTranscriptEmailDialog}
        meeting={selectedMeetingForEmail}
        transcript={selectedTranscriptForEmail}
        contentType="transcript"
        title="Envoyer la transcription par email"
      />

      {/* Dialog de création de nouvelle réunion */}
      <NewMeetingDialog
        open={newMeetingDialogOpen}
        onClose={() => setNewMeetingDialogOpen(false)}
        onSuccess={handleNewMeetingSuccess}
      />

      {/* Dialog d'édition de réunion */}
      <EditMeetingDialog
        open={editMeetingDialogOpen}
        onClose={handleCloseEditMeetingDialog}
        meetingId={editingMeetingId}
        onSuccess={handleEditMeetingSuccess}
      />

      {/* FAB flottant pour mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="Nouvelle réunion"
          onClick={handleAddMeeting}
          sx={{
            position: 'fixed',
            bottom: isXs ? 80 : 24, // Plus haut sur mobile à cause du bottom navigation
            right: 24,
            zIndex: 1000,
            background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
            boxShadow: '0 8px 24px rgba(63, 81, 181, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 100%)',
              transform: 'scale(1.1)',
              boxShadow: '0 12px 32px rgba(63, 81, 181, 0.5)'
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Bottom Navigation pour mobile */}
      {isXs && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1000,
            borderTop: '1px solid',
            borderColor: 'divider'
          }} 
          elevation={8}
        >
          <BottomNavigation
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiBottomNavigationAction-root': {
                fontSize: '0.7rem',
                minWidth: 'auto',
                paddingTop: '6px'
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                '&.Mui-selected': {
                  fontSize: '0.75rem'
                }
              }
            }}
          >
            <BottomNavigationAction
              label="Mes réunions"
              value="user"
              icon={<MicIcon />}
            />
            <BottomNavigationAction
              label="Partagées"
              value="shared"
              icon={<ShareIcon />}
            />
            {isAdmin && (
              <BottomNavigationAction
                label="Projets"
                value="projects"
                icon={<FolderIcon />}
              />
            )}
            {isAdmin && (
              <BottomNavigationAction
                label="Toutes"
                value="all"
                icon={<PeopleAltIcon />}
              />
            )}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default MeetingsListPage; 